import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { FavoriteHeartButton } from '@/components/favorites/favorite-heart-button'
import { ProjectHeroThumb } from '@/components/projects/project-hero-thumb'
import { useMyFavorites } from '@/features/favorites/use-favorites'
import { useMyFollowings } from '@/features/doer/use-doer'
import { canonicalProjectUrl } from '@/lib/canonical-url'
import type { SavedProjectSummary } from '@/api/types'

const PAGE_SIZE = 20

const formatRelative = (iso: string) => {
  const then = new Date(iso).getTime()
  const seconds = Math.max(0, (Date.now() - then) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}

const SavedProjectCard = ({ summary }: { summary: SavedProjectSummary }) => {
  const navigate = useNavigate()
  const url = canonicalProjectUrl(summary.author.handle, summary.slug)
  return (
    <Card
      className="relative cursor-pointer transition-colors hover:bg-accent"
      onClick={() => navigate(url)}
      role="link"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          navigate(url)
        }
      }}
    >
      <div className="absolute right-2 top-2 z-10">
        <FavoriteHeartButton
          projectId={summary.projectId}
          isFavoritedByMe={true}
          variant="overlay"
        />
      </div>
      <CardHeader>
        <div className="flex min-w-0 items-center gap-3">
          <ProjectHeroThumb
            thumbUrl={summary.heroThumbUrl}
            fallbackSeed={summary.projectId}
            alt={`${summary.title} hero`}
          />
          <div className="min-w-0">
            <CardTitle className="truncate text-lg">{summary.title}</CardTitle>
            <CardDescription>
              By @{summary.author.handle} · saved {formatRelative(summary.favoritedAt)}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      {summary.descriptionExcerpt && (
        <CardContent className="text-sm text-muted-foreground">
          {summary.descriptionExcerpt}
        </CardContent>
      )}
    </Card>
  )
}

const SkeletonGrid = () => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
    {Array.from({ length: 3 }).map((_, i) => (
      <Skeleton key={i} className="h-40" />
    ))}
  </div>
)

const EmptyState = () => (
  <Card>
    <CardContent className="flex flex-col items-center justify-center gap-2 py-16 text-center">
      <h2 className="text-lg font-semibold">Nothing saved yet.</h2>
      <p className="max-w-sm text-sm text-muted-foreground">
        Tap the heart on any project&apos;s page to save it for later.
      </p>
    </CardContent>
  </Card>
)

/**
 * Phase 10 Saved tab. Server returns favorites visibility-filtered; the Doing-overlap filter is
 * applied client-side here by combining with `useMyFollowings()` — see PHASE10_STATUS.md for the
 * modulith-cycle rationale.
 */
export const SavedTabContent = () => {
  const [page, setPage] = useState(0)
  const saved = useMyFavorites({ page, size: PAGE_SIZE })
  const followings = useMyFollowings()

  if (saved.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Failed to load your saved projects</AlertTitle>
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>We couldn&apos;t reach the server. Try again?</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => saved.refetch()}
            disabled={saved.isFetching}
          >
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    )
  }

  if (saved.isLoading) return <SkeletonGrid />

  const startedSet = new Set((followings.data ?? []).map((f) => f.projectId))
  const visibleItems = (saved.data?.items ?? []).filter((s) => !startedSet.has(s.projectId))

  if (visibleItems.length === 0) return <EmptyState />

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {visibleItems.map((summary) => (
          <SavedProjectCard key={summary.projectId} summary={summary} />
        ))}
      </div>
      {saved.data && saved.data.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {saved.data.page + 1} of {saved.data.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={page + 1 >= saved.data.totalPages}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  )
}
